import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  RefreshCw,
  Check,
  X,
  Pause,
  Play,
  Trash2,
  ChevronDown,
  ChevronRight,
  Mail,
  Eye,
  MousePointerClick,
  MessageSquareReply,
  MailWarning,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { useCampaignDetail } from '../hooks/useCampaignDetail'
import { useRecipientTimeline } from '../hooks/useRecipientTimeline'
import { stepLabel } from '../utils/steps'
import type { CampaignRecipientStatus, CampaignStatus, CampaignTimelineEntryType } from '../types'

function getCurrentUserId(): string {
  try {
    return (JSON.parse(localStorage.getItem('user') || '{}') as { id?: string }).id || ''
  } catch {
    return ''
  }
}

type Props = {
  workspaceId: string
  workspaceRole: 'owner' | 'admin' | 'member'
}

const STATUS_LABELS: Record<CampaignStatus, string> = {
  pending_approval: 'Pending approval',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  rejected: 'Rejected',
}

const RECIPIENT_STATUS_LABELS: Record<CampaignRecipientStatus, string> = {
  active: 'In progress',
  exited_replied: 'Replied',
  exited_bounced: 'Bounced',
  exited_unsubscribed: 'Unsubscribed',
  completed: 'Completed',
}

const TIMELINE_ICONS: Record<CampaignTimelineEntryType, React.ElementType> = {
  sent: Mail,
  open: Eye,
  click: MousePointerClick,
  reply: MessageSquareReply,
  bounce: MailWarning,
}

const TIMELINE_LABELS: Record<CampaignTimelineEntryType, string> = {
  sent: 'Sent',
  open: 'Opened',
  click: 'Clicked',
  reply: 'Replied',
  bounce: 'Bounced',
}

function StatCard({ label, value }: { label: string; value: number }) {
  const { currentTheme: t } = useTheme()
  return (
    <div className="rounded-xl p-3.5 flex flex-col gap-0.5" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: t.mutedText }}>{label}</span>
      <span className="text-xl font-semibold" style={{ color: t.text }}>{value}</span>
    </div>
  )
}

const CampaignDetailSection: React.FC<Props> = ({ workspaceId, workspaceRole }) => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const { campaignId } = useParams<{ campaignId: string }>()
  const isOwner = workspaceRole === 'owner'
  const canManage = workspaceRole === 'owner' || workspaceRole === 'admin'
  const {
    campaign, recipients, rollup, loading, error, retry,
    actionPending, actionError, approve, reject, pause, resume, remove,
  } = useCampaignDetail(workspaceId, campaignId!)
  const timeline = useRecipientTimeline(workspaceId, campaignId!)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const handleDelete = async () => {
    const succeeded = await remove()
    if (succeeded) navigate('/dashboard/sales?tab=campaigns')
    else setDeleteConfirmOpen(false)
  }

  const toggleExpanded = (recipientId: string) => {
    if (expandedId === recipientId) {
      setExpandedId(null)
      return
    }
    setExpandedId(recipientId)
    if (!timeline.timelines[recipientId]) timeline.load(recipientId)
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="h-8 w-48 rounded-lg animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
        <div className="h-32 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
          <AlertTriangle size={24} style={{ color: t.errorText }} />
        </div>
        <div>
          <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load this campaign</p>
          <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error || 'Something went wrong.'}</p>
        </div>
        <button type="button" onClick={retry} className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium" style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}>
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    )
  }

  const statusColors: Record<CampaignStatus, { bg: string; text: string }> = {
    pending_approval: { bg: t.warningBackground, text: t.warningText },
    active: { bg: t.successBackground, text: t.successText },
    paused: { bg: t.hoverBackground, text: t.mutedText },
    completed: { bg: t.infoBackground, text: t.infoText },
    rejected: { bg: t.errorBackground, text: t.errorText },
  }
  const statusColor = statusColors[campaign.status]
  const canDelete = canManage || campaign.createdBy === getCurrentUserId()

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold" style={{ color: t.text }}>{campaign.name}</h1>
            <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
              {STATUS_LABELS[campaign.status]}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {campaign.status === 'pending_approval' && (
              <>
                <button
                  type="button"
                  disabled={actionPending || !isOwner}
                  onClick={() => void approve()}
                  title={isOwner ? undefined : 'Only the workspace owner can approve a campaign'}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
                  style={{ background: t.accentGradient ?? t.accent }}
                >
                  <Check size={13} />
                  Approve & start
                </button>
                <button type="button" disabled={actionPending} onClick={() => void reject()} className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20">
                  <X size={13} />
                  Reject
                </button>
              </>
            )}
            {campaign.status === 'active' && (
              <button type="button" disabled={actionPending} onClick={() => void pause()} className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60" style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}>
                <Pause size={13} />
                Pause
              </button>
            )}
            {campaign.status === 'paused' && (
              <button type="button" disabled={actionPending} onClick={() => void resume()} className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-60" style={{ background: t.accentGradient ?? t.accent }}>
                <Play size={13} />
                Resume
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                disabled={actionPending}
                onClick={() => setDeleteConfirmOpen(true)}
                title="Delete campaign"
                aria-label="Delete campaign"
                className="flex items-center justify-center h-8 w-8 rounded-lg disabled:opacity-60 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
        {campaign.status === 'pending_approval' && !isOwner && (
          <p className="text-[12px] mt-1.5" style={{ color: t.mutedText }}>
            Only the workspace owner can approve this campaign — you can still reject it.
          </p>
        )}
        {actionError && <p className="text-[12px] mt-1.5" style={{ color: t.errorText }}>{actionError}</p>}
      </div>

      {rollup && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            <StatCard label="Sent" value={rollup.sent} />
            <StatCard label="Opened" value={rollup.opened} />
            <StatCard label="Clicked" value={rollup.clicked} />
            <StatCard label="Replied" value={rollup.replied} />
            <StatCard label="Bounced" value={rollup.bounced} />
          </div>

          {rollup.perStep.length > 0 && (
            <div className="mt-3 rounded-xl overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr style={{ backgroundColor: t.hoverBackground }}>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: t.mutedText }}>Step</th>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: t.mutedText }}>Sent</th>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: t.mutedText }}>Opened</th>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: t.mutedText }}>Clicked</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.perStep.map((step) => (
                    <tr key={step.stepOrder} style={{ borderTop: `1px solid ${t.border}` }}>
                      <td className="px-3 py-2" style={{ color: t.text }}>{stepLabel(step.stepOrder)}</td>
                      <td className="px-3 py-2" style={{ color: t.text }}>{step.sent}</td>
                      <td className="px-3 py-2" style={{ color: t.text }}>{step.opened}</td>
                      <td className="px-3 py-2" style={{ color: t.text }}>{step.clicked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="text-sm font-semibold block mb-2" style={{ color: t.text }}>
          Recipients ({recipients.length})
        </label>

        {recipients.length === 0 ? (
          <p className="text-[12.5px]" style={{ color: t.mutedText }}>
            Not started yet — recipients appear here once this campaign is approved.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {recipients.map((recipient) => {
              const isExpanded = expandedId === recipient.id
              const entries = timeline.timelines[recipient.id]
              const isLoadingTimeline = !!timeline.loadingIds[recipient.id]
              const timelineError = timeline.errors[recipient.id]

              return (
                <div key={recipient.id} className="rounded-xl" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
                  <button type="button" onClick={() => toggleExpanded(recipient.id)} className="flex items-center justify-between gap-3 p-3 w-full text-left">
                    <div className="flex items-center gap-2 min-w-0">
                      {isExpanded ? <ChevronDown size={14} style={{ color: t.mutedText }} /> : <ChevronRight size={14} style={{ color: t.mutedText }} />}
                      <span className="text-[13px] font-medium truncate" style={{ color: t.text }}>{recipient.email}</span>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}>
                      {RECIPIENT_STATUS_LABELS[recipient.status]}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="px-3.5 pb-3.5 flex flex-col gap-1.5">
                      {isLoadingTimeline && <span className="text-[12px]" style={{ color: t.mutedText }}>Loading…</span>}
                      {timelineError && <span className="text-[12px]" style={{ color: t.errorText }}>{timelineError}</span>}
                      {entries?.length === 0 && <span className="text-[12px]" style={{ color: t.mutedText }}>No activity yet.</span>}
                      {entries?.map((entry, i) => {
                        const Icon = TIMELINE_ICONS[entry.type]
                        return (
                          <div key={i} className="flex items-center gap-2 text-[12.5px]" style={{ color: t.text }}>
                            <Icon size={13} style={{ color: t.mutedText }} />
                            <span className="font-medium">{TIMELINE_LABELS[entry.type]}</span>
                            <span style={{ color: t.mutedText }}>{stepLabel(entry.stepOrder).toLowerCase()} · {new Date(entry.occurredAt).toLocaleString()}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{campaign.name}"?</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: t.mutedText }}>
            {campaign.status === 'active'
              ? 'This campaign is currently sending. Deleting it stops all remaining emails immediately and removes every recipient, tracked event, and analytics record for it. This cannot be undone.'
              : 'This removes every recipient, tracked event, and analytics record for this campaign. This cannot be undone.'}
          </p>
          {actionError && <p className="text-[12px]" style={{ color: t.errorText }}>{actionError}</p>}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(false)}
              className="flex items-center justify-center h-9 px-3.5 rounded-lg text-sm font-medium"
              style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={actionPending}
              onClick={() => void handleDelete()}
              className="flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60 bg-red-500 hover:bg-red-600"
            >
              <Trash2 size={14} />
              {actionPending ? 'Deleting…' : 'Delete campaign'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(CampaignDetailSection)
